/* eslint-disable no-undef */
import React, { useEffect, useState, useRef } from "react";
import { Room } from "../../../api";
import { useAuth } from "../../../hooks";
import RoomDetailModal from "../../../components/Student/LiveClass/RoomDetailModal";
import { Select, Input, Button, Icon } from "semantic-ui-react";
import { BasicModal } from "../../../components/Shared";
import { ListLiveClass } from "../../../components/Student/LiveClass/ListLiveClass/ListLiveClass";
import "./LiveClass.scss";

const liveClassController = new Room();

export function LiveClass() {
  const { user, accessToken } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("scheduled");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({});
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [jitsiOpen, setJitsiOpen] = useState(false);
  const [jitsiRoom, setJitsiRoom] = useState("");
  const jitsiContainerRef = useRef(null);
  const [jitsiApi, setJitsiApi] = useState(null);

  const handleItemsPerPageChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  useEffect(() => {
    async function fetchRooms() {
      setLoading(true);
      setError(null);
      try {
        const params = {
          status: status || undefined,
          search: search || undefined,
          page: currentPage,
          limit: limit,
          orderBy: "scheduledAt",
          orderDirection: "ASC",
        };
        const res = await liveClassController.getRooms(accessToken, params);
        setRooms(res.data);
        setPagination(res.pagination);
      } catch (err) {
        setError("No se pudieron cargar las clases en vivo.");
      }
      setLoading(false);
    }
    fetchRooms();
  }, [status, search, currentPage, limit, accessToken]);

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleRowClick = (room) => {
    setSelectedRoom(room);
    setModalOpen(true);
  };
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedRoom(null);
  };

  // Funciones para estado
  const getStatusColor = (val) => {
    switch (val) {
      case "active":
        return "green";
      case "scheduled":
        return "blue";
      case "ended":
        return "grey";
      default:
        return "red";
    }
  };

  const getStatusText = (val) => {
    switch (val) {
      case "scheduled":
        return "PROGRAMADA";
      case "active":
        return "EN PROGRESO";
      case "ended":
        return "FINALIZADA";
      default:
        return "CANCELADA";
    }
  };

  // Función para inicializar la API de JaaS
  const initJitsi = (roomName) => {
    console.log("Inicializando JaaS con sala:", roomName);
    try {
      if (!window.JitsiMeetExternalAPI) {
        console.log("Cargando JitsiMeetExternalAPI");
        // Cargar el script de JaaS si aún no está cargado
        const script = document.createElement("script");
        script.src = "https://8x8.vc/external_api.js";
        script.async = true;
        script.onload = () => {
          console.log("Script JaaS cargado exitosamente");
          createJitsiMeeting(roomName);
        };
        script.onerror = (error) => {
          console.error("Error al cargar el script de JaaS:", error);
          alert(
            "No se pudo cargar la biblioteca de videollamadas. Por favor, intenta abrir en una nueva pestaña."
          );
        };
        document.body.appendChild(script);
      } else {
        console.log("JitsiMeetExternalAPI ya está cargado");
        // Si ya está cargado, iniciar la reunión directamente
        createJitsiMeeting(roomName);
      }
    } catch (error) {
      console.error("Error en initJitsi:", error);
    }
  };

  // Crear la reunión con la API de JaaS
  const createJitsiMeeting = (roomName) => {
    console.log("Creando reunión Jitsi para sala:", roomName);

    if (!jitsiContainerRef.current) {
      console.error("Error: jitsiContainerRef no está disponible");
      return;
    }

    // Limpiar cualquier sesión anterior
    if (jitsiApi) {
      console.log("Limpiando sesión anterior de Jitsi");
      jitsiApi.dispose();
      setJitsiApi(null);
    }

    try {
      // Verificar que la API de JitsiMeetExternalAPI esté disponible
      if (!window.JitsiMeetExternalAPI) {
        console.error("Error: JitsiMeetExternalAPI no está cargado");
        return;
      }

      // Configuración de la API de JaaS
      // Usamos la variable de entorno definida en .env
      const domain = "8x8.vc";
      const options = {
        roomName: roomName,
        width: "100%",
        height: "100%",
        parentNode: jitsiContainerRef.current,
        lang: "es",
        appId: process.env.JITSI_APPID, // Usando la variable de entorno existente
        jwt: null, // Puedes generar un JWT para autenticación desde tu backend
        configOverwrite: {
          prejoinPageEnabled: false,
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          defaultLanguage: "es",
        },
        interfaceConfigOverwrite: {
          TOOLBAR_BUTTONS: [
            "microphone",
            "camera",
            "closedcaptions",
            "desktop",
            "fullscreen",
            "fodeviceselection",
            "hangup",
            "profile",
            "chat",
            "recording",
            "livestreaming",
            "etherpad",
            "sharedvideo",
            "settings",
            "raisehand",
            "videoquality",
            "filmstrip",
            "feedback",
            "stats",
            "shortcuts",
            "tileview",
            "videobackgroundblur",
            "download",
            "help",
            "mute-everyone",
            "security",
          ],
        },
      };

      console.log("Opciones de configuración JaaS:", {
        domain,
        roomName,
        appId: process.env.JITSI_APPID ? "Configurado" : "No configurado",
      });

      // Crear la instancia de JaaS
      const api = new window.JitsiMeetExternalAPI(domain, options);
      setJitsiApi(api);

      // Manejar eventos
      api.addEventListeners({
        readyToClose: () => {
          setJitsiOpen(false);
          if (jitsiApi) {
            jitsiApi.dispose();
            setJitsiApi(null);
          }
        },
        participantLeft: () => console.log("Participante salió"),
        participantJoined: () => console.log("Participante entró"),
        videoConferenceJoined: () =>
          console.log("Te has unido a la conferencia"),
        videoConferenceLeft: () => {
          console.log("Has dejado la conferencia");
          setJitsiOpen(false);
        },
      });

      // Configurar el nombre del usuario
      if (user) {
        api.executeCommand("displayName", `${user.firstname} ${user.lastname}`);
      }
    } catch (error) {
      console.error("Error al crear la reunión Jitsi:", error);
    }
  };

  // Función para unirse a la reunión
  const joinMeeting = (row) => {
    if (row.meetingUrl) {
      console.log("Procesando URL de sala:", row.meetingUrl);

      // Extraer el nombre de la sala de la URL de Jitsi
      const extractRoomName = (url) => {
        try {
          // Normalizar la URL: eliminar parámetros y fragmentos
          const cleanUrl = url.split("?")[0].split("#")[0];
          console.log("URL limpia:", cleanUrl);

          // Si es una URL de JaaS, extraer el nombre de la sala
          if (cleanUrl.includes("8x8.vc")) {
            // Ejemplo: https://8x8.vc/edunova/nombre-sala
            const urlParts = cleanUrl.split("/");
            const roomName =
              urlParts[urlParts.length - 1] || `edunova-${Date.now()}`;
            console.log("Sala extraída de 8x8.vc:", roomName);
            return roomName;
          }
          // Si es una URL de Jitsi estándar
          else if (cleanUrl.includes("meet.jit.si")) {
            // Ejemplo: https://meet.jit.si/nombre-sala
            const urlParts = cleanUrl.split("/");
            const roomName =
              urlParts[urlParts.length - 1] || `edunova-${Date.now()}`;
            console.log("Sala extraída de meet.jit.si:", roomName);
            return roomName;
          }

          // Si no coincide con ninguno de los dominios conocidos,
          // verificar si tiene formato de URL o es solo un nombre de sala
          if (cleanUrl.includes("http")) {
            // Es una URL pero no reconocemos el dominio, intentar extraer el último segmento
            const urlParts = cleanUrl.split("/");
            const roomName =
              urlParts[urlParts.length - 1] || `edunova-${Date.now()}`;
            console.log("Sala extraída de URL desconocida:", roomName);
            return roomName;
          }

          // Si es solo un nombre de sala
          console.log("Usando el texto completo como nombre de sala:", url);
          return url;
        } catch (error) {
          console.error("Error al extraer el nombre de la sala:", error);
          return "edunova-" + Date.now(); // Fallback a un nombre genérico
        }
      };

      const roomName = extractRoomName(row.meetingUrl);
      setJitsiRoom(roomName);
      setJitsiOpen(true);
    }
  };

  // Inicializar Jitsi cuando se abre el modal
  useEffect(() => {
    if (jitsiOpen && jitsiRoom) {
      initJitsi(jitsiRoom);
    }

    // Limpiar cuando se cierra el modal
    return () => {
      if (jitsiApi) {
        jitsiApi.dispose();
        setJitsiApi(null);
      }
    };
  }, [jitsiOpen, jitsiRoom]);

  // Verificar la disponibilidad de la variable de entorno
  useEffect(() => {
    if (!process.env.JITSI_APPID) {
      console.warn(
        "¡Advertencia! No se encontró la variable de entorno JITSI_APPID. El embebido de JaaS puede no funcionar correctamente."
      );
    }
  }, []);

  // Configuración para CustomTable
  const tableConfig = {
    headers: [
      "Nombre",
      "Descripción",
      "Fecha y Hora",
      "Duración",
      "Profesor",
      "Estado",
      "Acción",
    ],
    fields: [
      {
        field: "name",
        dataType: "text",
        align: "left",
        getColor: () => "blue",
      },
      {
        field: "description",
        dataType: "text",
        align: "left",
      },
      {
        field: "scheduledAt",
        dataType: "date",
        format: "DD/MM/YYYY HH:mm",
        align: "center",
      },
      {
        field: "durationMinutes",
        dataType: "text",
        align: "center",
        getText: (val) => `${val} min`,
      },
      {
        field: "User.firstname+User.lastname",
        dataType: "text",
        align: "left",
        getText: (val, row) =>
          row.User ? `${row.User.firstname} ${row.User.lastname}` : "-",
      },
      {
        field: "status",
        component: "Label",
        align: "center",
        getColor: getStatusColor,
        getText: getStatusText,
      },
      {
        buttons: [
          {
            color: "green",
            icon: "video",
            placeholder: "Unirse a la clase",
            onClick: joinMeeting,
          },
          {
            color: "blue",
            icon: "external",
            placeholder: "Abrir en nueva pestaña",
            onClick: (row) => {
              if (typeof window !== "undefined" && row.meetingUrl) {
                window.open(row.meetingUrl, "_blank", "noopener,noreferrer");
              }
            },
          },
        ],
      },
    ],
  };

  // Opciones para el filtro de estado
  const statusOptions = [
    { key: "", value: "", text: "Todos" },
    { key: "scheduled", value: "scheduled", text: "Programada" },
    { key: "active", value: "active", text: "En progreso" },
    { key: "ended", value: "ended", text: "Finalizada" },
    { key: "cancelled", value: "cancelled", text: "Cancelada" },
  ];

  return (
    <div className="student-live-classes">
      <h2 className="student-live-classes__title">Clases en Vivo</h2>
      <p className="student-live-classes__description">
        Accede a tus clases virtuales programadas, en progreso y finalizadas.
        Haz clic en una fila para ver detalles o unirte si la clase está activa.
      </p>
      <div className="student-live-classes__filters">
        <Select
          className="edunova-select"
          options={statusOptions}
          value={status}
          onChange={(e, { value }) => handleStatusChange({ target: { value } })}
          placeholder="Selecciona estado"
          clearable
        />
        <Input
          icon="search"
          iconPosition="left"
          type="text"
          placeholder="Buscar por nombre o descripción..."
          value={search}
          onChange={handleSearchChange}
        />
        <Button
          onClick={() => {
            setStatus("");
            setSearch("");
            setCurrentPage(1);
          }}
        >
          <Icon name="refresh" />
          Limpiar filtros
        </Button>
      </div>
      <ListLiveClass
        rooms={rooms}
        loading={loading}
        error={error}
        pagination={pagination}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onRowClick={handleRowClick}
        tableConfig={tableConfig}
      />
      <RoomDetailModal
        room={selectedRoom}
        open={modalOpen}
        onClose={handleModalClose}
      />
      <BasicModal
        size="large"
        title="Clase en Vivo"
        show={jitsiOpen}
        close={() => {
          setJitsiOpen(false);
          if (jitsiApi) {
            jitsiApi.dispose();
            setJitsiApi(null);
          }
        }}
        className="jitsi-modal"
      >
        <div
          className="jitsi-container"
          ref={jitsiContainerRef}
          style={{ height: "70vh" }}
        >
          {/* El API de JaaS creará el iframe aquí */}
        </div>
        <div
          className="jitsi-fallback"
          style={{ marginTop: "20px", textAlign: "center" }}
        >
          <p>Si tienes problemas para acceder a la clase, puedes:</p>
          <Button
            color="blue"
            icon="external"
            content="Abrir en nueva pestaña"
            onClick={() => {
              // Abrir en una nueva pestaña si hay problemas
              if (jitsiRoom) {
                const fullUrl = `https://8x8.vc/edunova/${jitsiRoom}`;
                window.open(fullUrl, "_blank", "noopener,noreferrer");
              }
            }}
          />
        </div>
      </BasicModal>
    </div>
  );
}
